/**
 * Shared Application Header Component
 * 
 * Provides consistent navigation and header across all pages in the application.
 * Features:
 * - Logo and branding
 * - Main navigation menu with active page highlighting
 * - Action items (like New Trade) with icons as standard navigation links
 * - Theme toggle
 * - User profile avatar with first letter of name/email
 * - User profile/settings access
 * - Mobile-responsive design
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from '../ui/ThemeToggle';
import { Logo } from '../ui/Logo';
import { BarChart3, Sparkles, Settings, Bug, Building, ListChecks, Newspaper } from 'lucide-react';

interface AppHeaderProps {
  /** Custom page title to display below the header */
  pageTitle?: string;
  /** Custom page description */
  pageDescription?: string;
}

export function AppHeader({ 
  pageTitle,
  pageDescription 
}: AppHeaderProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigation items configuration
  const navigationItems = [
    { path: '/dashboard', label: 'Dashboard', exact: true, icon: BarChart3 },
    { path: '/ai-analyst', label: 'AI', icon: Sparkles },
    { path: '/news', label: 'News', icon: Newspaper },
    { path: '/webhook-stocks', label: 'Stocks', icon: ListChecks },
  ];

  // Special action items (like New Trade) - removed, now in individual pages
  const actionItems: any[] = [];

  // Check if a navigation item is active
  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Main Header */}
      <div className="mb-8">
        {/* Logo and profile navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/dashboard" className="hover:opacity-80 transition-opacity">
            <Logo className="h-8 w-24" />
          </Link>
          <div className="flex items-center space-x-6">
            {/* Navigation Links */}
            <nav className="hidden sm:flex items-center space-x-6">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`font-medium transition-all duration-200 px-3 py-2 rounded-md inline-flex items-center ${
                      isActive(item.path, item.exact)
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            
            {/* Theme toggle and profile */}
            <div className="flex items-center space-x-6">
              <ThemeToggle />
              
              {/* Profile Dropdown - Hidden on mobile, shown on desktop */}
              <div className="relative hidden sm:block" ref={dropdownRef}>
                <motion.button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="rounded-full hover:scale-105 transition-all duration-200"
                  title="Account Settings"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                    {user?.firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                </motion.button>
                
                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div 
                      className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-md shadow-lg py-2 z-50 border border-gray-200 dark:border-gray-700"
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                      >
                        <Link
                          to="/positions"
                          className="block w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 inline-flex items-center"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          <Building className="h-4 w-4 mr-3" />
                          Brokerages
                        </Link>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: 0.15 }}
                      >
                        <Link
                          to="/settings"
                          className="block w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 inline-flex items-center"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          <Settings className="h-4 w-4 mr-3" />
                          Settings
                        </Link>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: 0.2 }}
                      >
                        <Link
                          to="/debug"
                          className="block w-full px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 inline-flex items-center"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          <Bug className="h-4 w-4 mr-3" />
                          Debug
                        </Link>
                      </motion.div>
                      <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                      <motion.button
                        onClick={() => {
                          logout();
                          setIsProfileDropdownOpen(false);
                        }}
                        className="block w-full text-left px-5 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: 0.3 }}
                      >
                        Logout
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu - Now handled by BottomNavigation component */}
      </div>

      {/* Page Title Section */}
      {(pageTitle || pageDescription) && (
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {pageTitle && (
            <motion.h1 
              className="text-3xl font-bold text-gray-900 dark:text-white"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              {pageTitle}
            </motion.h1>
          )}
          {pageDescription && (
            <motion.p 
              className="mt-2 text-gray-600 dark:text-gray-400"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              {pageDescription}
            </motion.p>
          )}
        </motion.div>
      )}
    </>
  );
}
